<template>
    <common-modal height="medium" width="large" @emitEvent="emitEvent($event, ['close'])" @close="emitEvent({ name: 'close' })">
        <div class="content">
            <div class="patient-details">
                <common-content-spacer height="medium">
                    <common-page-header :heading="patientFullName"></common-page-header>
                    <div class="patient-details-wrapper">
                        <div class="patient-details-wrapper--item">
                            <p>{{ $t('pages.referrals.patient_details.date_of_birth') }} {{ referral.patient.dateOfBirth }}</p>
                            <p class="patient-details-wrapper--item__info-icon">
                                <common-icon name="phone" size="small"></common-icon>
                                {{ referral.patient.mobilePhone }}
                            </p>
                        </div>
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--location">
                            <p class="patient-details-wrapper--item--location__icon">
                                <common-icon name="location" size="huge"></common-icon>
                            </p>
                            <div>
                                <p>
                                    {{ referral.practiceLocation.address }}
                                </p>
                                <p>
                                    {{ referral.practiceLocation.zip }} {{ referral.practiceLocation.city }},
                                    {{ referral.practiceLocation.state }}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div class="patient-details-wrapper">
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--insurance">
                            <common-icon name="insurance" size="medium"></common-icon>
                            <div class="patient-details-wrapper--item--insurance__info">
                                <p>
                                    <span class="patient-details-wrapper--item__heading">
                                        {{ $t('pages.referrals.patient_details.insurance') }}
                                    </span>
                                </p>
                                <p>
                                    {{ referral.insurancePlan.name }}
                                </p>
                                <p>{{ $t('pages.referrals.patient_details.group') }} {{ referral.insuranceGroupNumber }}</p>
                                <p>{{ $t('pages.referrals.patient_details.id') }} {{ referral.insuranceNumber }}</p>
                            </div>
                        </div>
                        <div class="patient-details-wrapper--item patient-details-wrapper--item--insurance">
                            <common-user-avatar :user="referral.primaryCarePhysician" size="label"></common-user-avatar>
                            <div class="patient-details-wrapper--item--insurance__info">
                                <p class="patient-details-wrapper--item--avatar">
                                    <span class="patient-details-wrapper--item__heading">
                                        {{ $t('pages.referrals.patient_details.primary_care') }}
                                    </span>
                                </p>
                                <p>{{ referral.primaryCarePhysician.name }}</p>
                                <p class="patient-details-wrapper--item__info-icon">
                                    <common-icon name="email" size="small"></common-icon>
                                    {{ referral.primaryCarePhysician.email }}
                                </p>
                                <p class="patient-details-wrapper--item__info-icon">
                                    <common-icon name="phone" size="small"></common-icon>
                                    {{ referral.primaryCarePhysician.phone }}
                                </p>
                            </div>
                        </div>
                    </div>
                </common-content-spacer>
            </div>
            <div>
                <span class="patient-details-wrapper--item__heading">
                    {{ $t('pages.referrals.patient_details.clinical_history') }}
                </span>
            </div>
            <div class="patient-details-wrapper patient-details-wrapper--history">
                <div
                    class="patient-details-wrapper--item patient-details-wrapper--item__full-width patient-details-wrapper--item--insurance"
                >
                    <common-icon name="calendar" size="medium"></common-icon>
                    <div class="patient-details-wrapper--item--insurance__info">
                        <p>
                            <span class="patient-details-wrapper--item__heading">{{ appointmentDateTime }}</span>
                        </p>
                        <p>
                            {{ referral.diagnosis }}
                        </p>
                        <p v-if="referral.referralNotes && referral.referralNotes.length">
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.notes') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images
                                    ref="images"
                                    :images="referral.referralNotes"
                                    :showMoreOption="true"
                                ></common-images>
                            </common-summary-item>
                        </p>
                        <p v-if="referral.consultationReports && referral.consultationReports.length">
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.consultation_report') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images :images="referral.consultationReports" :showMoreOption="true"></common-images>
                            </common-summary-item>
                        </p>
                        <p
                            v-if="referral.attachments && referral.attachments.length"
                            class="patient-details-wrapper--attachments"
                        >
                            <span class="patient-details-wrapper--item__heading">
                                {{ $t('common.referral_details.message_attachments') }}
                            </span>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images :images="referral.attachments" :showMoreOption="true"></common-images>
                            </common-summary-item>
                        </p>
                    </div>
                </div>
            </div>
            <div v-if="referral.note" class="patient-details-wrapper--notes">
                <span class="patient-details-wrapper--item__heading">{{ $t('pages.referrals.patient_details.note') }}</span>
                <common-quote
                    :class="referral.shouldShowPatientInfo ? '' : 'text-blured'"
                    :content="referral.note"
                    :author="referringPhysicianName"
                ></common-quote>
            </div>
            <div v-if="userId" @click="redirectToChatModal()">
                <common-icon size="gigantic" name="msg"></common-icon>
                <span class="referral-show-details__send-text">
                    {{ $t('common.referral_details.send_message', { name: referringPhysicianName }) }}
                </span>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import dayjs from 'dayjs'
    import { composeDisplayName } from 'components/common/CommonUserName'

    export default {
        props: {
            referral: { type: Object, required: true },
        },
        data() {
            return {
                userId: this.$auth.getUser().id,
            }
        },
        computed: {
            patientFullName() {
                return this.referral.patient.firstName + ' ' + this.referral.patient.lastName
            },

            appointmentDateTime() {
                return dayjs(this.referral.appointmentDateTime).format('MMM DD, YYYY')
            },

            referringPhysicianName() {
                return composeDisplayName(this.referral.fromUser)
            },
        },

        methods: {
            redirectToChatModal() {
                this.emitEvent({ name: 'close' })
                this.$router.push({
                    name: `referrals.${this.$route.params.type}.show`,
                    params: { id: this.referral.id },
                    query: { openChat: true },
                })
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/page-elements/referrals/_referrals-patient-details-modal';
</style>
